// Payment-option helpers shared across the Builder, Present Mode, the
// patient-at-home view, and the Settings editor preview.
//
// Descriptions support {token} placeholders that interpolate the option's
// own numeric fields at render time, so editing (e.g.) the discount %
// updates the prose everywhere without the practice re-typing the sentence.
// Mirrors the {placeholder} convention already used by the delivery
// (SMS / email) templates.
//
// Supported tokens:
//   {discount}    — discount percentage (discount-type options)
//   {splits}      — number of payments (split / in-house options)
//   {months}      — financing term in months (finance options)
//   {apr}         — annual percentage rate (finance options)
//   {fee}         — additional-fee amount with unit ($ or %)
//   {downpayment} — chairside "Pay today" amount (when present)
//
// Unknown tokens are left as-is (e.g., "{foo}") so a typo is visible in the
// live preview rather than silently blanked out on the patient's screen.

import { isCherryBacked, hasUsableCherryTerms, cherryAmountFor, cherryHeadline } from './cherryTerms.js';

export const PAYMENT_DESC_TOKENS = ['discount', 'splits', 'months', 'apr', 'fee', 'downpayment'];

// The tokens that print the practice's TYPED financing figures. A Cherry-backed option shows the
// lender's real terms instead, so a description using either one would put a number on the card
// that Cherry never offered — interpolatePaymentDesc blanks such a description entirely.
export const FINANCE_DESC_TOKENS = ['months', 'apr'];

/** Does this description text use a token that prints a typed financing figure? */
export function descUsesFinanceTokens(desc) {
  if (typeof desc !== 'string') return false;
  return (desc.match(/\{(\w+)\}/g) || []).some((m) => FINANCE_DESC_TOKENS.includes(m.slice(1, -1)));
}

function fmtTokenValue(opt, token) {
  if (token === 'fee') {
    const amt = parseFloat(opt && opt.feeAmount);
    if (!amt || amt <= 0) return '';
    const unit = (opt && opt.feeUnit) === '%' ? '%' : '$';
    return unit === '%' ? `${amt}%` : `$${amt}`;
  }
  if (token === 'downpayment') {
    const amt = parseFloat(opt && opt.downpayment);
    if (!amt || amt <= 0) return '';
    return `$${amt}`;
  }
  if (token === 'months') {
    // Multi-tier financing has no single term; resolve {months} to the first
    // configured term (reduced-APR first, else deferred) for the desc blurb.
    const direct = opt && opt.months;
    if (direct !== null && direct !== undefined && direct !== '') return String(direct);
    const apr = Array.isArray(opt && opt.aprMonths) ? opt.aprMonths : [];
    const def = Array.isArray(opt && opt.deferredMonths) ? opt.deferredMonths : [];
    const first = apr.length ? apr[0] : (def.length ? def[0] : null);
    return first === null || first === undefined ? '' : String(first);
  }
  const v = opt && opt[token];
  if (v === null || v === undefined || v === '') return '';
  return String(v);
}

/**
 * Substitute {token} placeholders in an option's description with the
 * option's live field values. Returns the desc unchanged when there are
 * no tokens, so legacy descriptions (plain prose) render exactly as before.
 *
 * The optional `extras` lets the chairside Present-Mode flow inject the
 * downpayment (which lives on the plan, not the option) so descriptions
 * can reference it without the renderer mutating the option object.
 */
export function interpolatePaymentDesc(opt, extras) {
  if (!opt || typeof opt.desc !== 'string') return (opt && opt.desc) || '';
  // A Cherry-backed option never prints typed financing figures, and a sentence built around
  // "{months} months at {apr}%" is wrong without them — so the whole description is withheld
  // rather than half-rendered. Settings warns the office when this will happen.
  if (isCherryBacked(opt) && descUsesFinanceTokens(opt.desc)) return '';
  const lookup = extras ? { ...opt, ...extras } : opt;
  return opt.desc.replace(/\{(\w+)\}/g, (match, token) => {
    if (PAYMENT_DESC_TOKENS.includes(token)) {
      return fmtTokenValue(lookup, token);
    }
    return match;
  });
}

/**
 * Refresh practice-wide flags on a per-patient options array from the live
 * Settings → Payment Options. The per-patient `PaymentOptionOverrides` blob
 * snapshots options at the moment of first customization, so any flag the
 * OFFICE controls (not the TC) goes stale the instant settings change. Two
 * historical examples that bit us:
 *
 *   • `recommended` ("Most Popular") — marketing flag; was showing on the
 *     wrong card because the snapshot froze the old value.
 *   • `feeEnabled` / `downpaymentEnabled` — toggling these on in Settings did
 *     nothing for any patient who already had overrides until they hit
 *     "Reset to defaults."
 *
 * So we always re-overlay practice-meta fields by option id. Anything truly
 * per-patient (discount %, splits, months, terms, title, desc, terms, AND
 * `feeAmount`) is preserved from the override. Custom one-off options (no
 * settings match by id) keep their existing values for these flags except
 * `recommended`, which is forced to false.
 */
// Finance tiers (deferredMonths/aprMonths/apr) are practice-wide informational
// config, NOT per-patient — so they re-overlay from live settings too, or a
// patient with an old override snapshot would show stale tiers after the office
// edits them. (Keep this list in sync with the backend twins in
// tx-presenter-patient.js + tx-presenter-worker.js.)
//
// `cherryHiddenPlans` (the plans the practice switched off, Settings → Your Cherry plans) is the
// same kind of practice-wide choice, and a stale snapshot of it would keep showing a patient a
// plan the office has since switched off. Settings writes it explicitly (even as []) on a Cherry
// option so an emptied list clears the snapshot too — this overlay only copies keys the live
// option actually has. `cherryTermMonths` is the legacy allow-list it replaced (still honoured
// by cherryPlansFor, no longer written), so it stays here for any blob that still carries one.
//
// ⛔ `cherryHiddenPlansPatient` (the plans hidden for ONE patient in the Builder) is deliberately
// NOT in this list: it is per-patient like `feeAmount` and lives only in PaymentOptionOverrides.
// Adding it here would re-overlay it from Settings (where it never exists) and wipe it on every
// read. cherryPlansFor unions the two lists.
//
// `provider` is here for a sharper reason than staleness. It decides whether an option shows
// a LENDER'S REAL TERMS or the practice's typed estimate. Every override snapshot written
// before 2026-08-06 predates the field entirely, so without this overlay those patients would
// keep rendering the typed 14.9% after the office connected Cherry — silently, and only for
// patients whose options had ever been customised. Which patients see true terms must never
// depend on when their plan was last edited.
const PRACTICE_META_FIELDS = ['feeEnabled', 'feeDesc', 'feeUnit', 'downpaymentEnabled', 'deferredMonths', 'aprMonths', 'apr', 'provider', 'cherryTermMonths', 'cherryHiddenPlans'];

export function overlayRecommendedFromSettings(options, settingsOptions) {
  const settingsById = {};
  (settingsOptions || []).forEach((o) => {
    if (o && o.id) settingsById[o.id] = o;
  });
  return (options || []).map((o) => {
    const setOpt = o && o.id ? settingsById[o.id] : null;
    const out = { ...o };
    out.recommended = setOpt ? !!setOpt.recommended : false;
    if (setOpt) {
      PRACTICE_META_FIELDS.forEach((f) => {
        if (Object.prototype.hasOwnProperty.call(setOpt, f)) out[f] = setOpt[f];
      });
    }
    return out;
  });
}

/**
 * Resolve an option's Additional Fee to a dollar number against the patient
 * portion. `%` is computed off the patient portion (not totalDue) so the fee
 * is predictable when the math chain composes with a downpayment.
 *
 *   feeUnit '$' → feeAmount is the dollar value
 *   feeUnit '%' → feeAmount is a percent of base (e.g., 2.5 → 2.5% of base)
 *
 * Returns a non-negative number; zero when the feature isn't enabled or no
 * amount is set.
 */
export function resolveFeeAmount(opt, patientPortion) {
  if (!opt || !opt.feeEnabled) return 0;
  const amt = parseFloat(opt.feeAmount);
  if (!amt || amt <= 0) return 0;
  if ((opt.feeUnit || '$') === '%') {
    const base = Math.max(0, parseFloat(patientPortion) || 0);
    return Math.max(0, (base * amt) / 100);
  }
  return Math.max(0, amt);
}

/**
 * Does this option offer the chairside "Pay today" downpayment?
 *
 * The office turns it on per option in Settings; pay-in-full (discount) never
 * takes one, because paying in full IS the payment.
 *
 * SINGLE SOURCE OF TRUTH — call this everywhere. This predicate used to be
 * written out by hand in three places, and one copy drifted into gating on the
 * breakdown `kind === 'split'`, which silently hid the input for every
 * FINANCING option (prod, 2026-08-04: an office enabled "Pay today" on their
 * 3rd-party financing plan and had nowhere to type the amount). Anything that
 * needs to know "can this option take a downpayment" asks here.
 */
export function canTakeDownpayment(opt) {
  return !!(opt && opt.downpaymentEnabled) && opt.type !== 'discount';
}

/**
 * Human-readable label for the Additional Fee, e.g.:
 *   "Card processing fee · 2.5%"        (percent)
 *   "Financing Fee"                     (flat dollar)
 * Returns empty when the fee isn't applicable.
 */
export function formatFeeLabel(opt) {
  if (!opt || !opt.feeEnabled) return '';
  const amt = parseFloat(opt.feeAmount);
  if (!amt || amt <= 0) return '';
  const desc = (opt.feeDesc && String(opt.feeDesc).trim()) || 'Additional fee';
  return (opt.feeUnit || '$') === '%' ? `${desc} · ${amt}%` : desc;
}

/**
 * Human label for one finance tier row, derived from its kind/apr:
 *   deferred → "6 Months Deferred Interest"
 *   apr      → "24 Months Reduced APR @ 14.9%"
 * Shared by the patient view, Present Mode, and the signed PDF (keep the
 * backend twin in lib/pdf/format.js identical).
 */
export function financeTierLabel(tier) {
  if (!tier) return '';
  const m = Math.max(1, parseInt(tier.months, 10) || 1);
  const apr = parseFloat(tier.apr) || 0;
  if (tier.kind === 'deferred' || apr <= 0) return `${m} Months Deferred Interest`;
  return `${m} Months Reduced APR @ ${apr}%`;
}

/**
 * The two lines of money that go ON a payment-option card: a headline the
 * patient reads at a glance and a smaller qualifying sub-line.
 *
 *   discount → "$9,171.30"          / "You save $482.70 (5%)"
 *   split    → "2 payments of $4,827.00" / "$100.00 today · $9,654.00 total"
 *   finance  → "As low as ~$229.00/mo"   / "60 months · estimate"
 *   cherry   → "As low as $63.70/mo"     / "60 monthly payments · 20.8% APR"
 *
 * A CHERRY-BACKED finance option (provider === 'cherry') is quoted from `cherryTerms` — the
 * lender's own plans, verbatim — and ONLY when those terms were priced for exactly this amount
 * (cherryAmountFor) and survive the option's term filter. Otherwise it returns null: no figure,
 * never the typed tiers. `cherryTerms` is `{ purchaseAmount, plans, fetchedAt }` or null.
 *
 * Returns null when there is nothing honest to put on the card: no option, a
 * custom (title/desc-only) option, a zero total, or a financing option with no
 * configured tiers. A null means "render no price block", NOT "render $0".
 *
 * `money` is the caller's formatter (number → string) so each screen keeps its
 * own currency style; omit it for a plain en-US "$1,234.56".
 *
 * SHARED by Present Mode (TxPresenterPresentMode.vue) and the patient-at-home
 * view (TxPatientView.vue) — the wording lives here and only here, so the two
 * screens can never drift into quoting the same plan differently. There is a
 * TWIN in the backend that renders the signed PDF:
 * tx-presenter-service/lib/pdf/format.js → paymentOptionAmountLines.
 * KEEP IN SYNC. The canonical fixture table is duplicated VERBATIM in both
 * repos' tests (tests/component/paymentOptionAmountLines.spec.js here,
 * tests/pdf-payment-card-amounts.test.js there) so a change to one repo that
 * isn't mirrored fails the other repo's suite.
 */
export function paymentOptionAmountLines(opt, patientPortion, extras, money, cherryTerms) {
  if (!opt) return null;
  const fmt = typeof money === 'function'
    ? money
    : (n) => `$${(parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const b = computePaymentBreakdown(opt, patientPortion, extras);
  if (b.kind === 'custom') return null;
  if (b.totalDue <= 0) return null;

  // "Pay today" is money already collected, so it prefixes the sub-line rather
  // than changing the headline (which quotes what's left to pay).
  const todayPrefix = b.downpayment > 0 ? `${fmt(b.downpayment)} today · ` : '';

  if (b.kind === 'discount') {
    return {
      headline: fmt(b.finalAmount),
      sub: b.discountPct > 0 ? `You save ${fmt(b.discountAmt)} (${b.discountPct}%)` : '',
    };
  }

  if (b.kind === 'split') {
    const noun = b.count === 1 ? 'payment' : 'payments';
    return {
      headline: `${b.count} ${noun} of ${fmt(b.perPayment)}`,
      sub: `${todayPrefix}${fmt(b.totalDue)} total`,
    };
  }

  // Cherry-backed finance — the lender's real figures or nothing. Checked BEFORE the typed
  // tiers are even looked at; the breakdown has none for a Cherry option anyway.
  if (b.cherry) {
    if (!hasUsableCherryTerms(opt, cherryTerms, cherryAmountFor(b))) return null;
    const h = cherryHeadline(cherryTerms, opt);
    return h ? { headline: h.headline, sub: `${todayPrefix}${h.sub}` } : null;
  }

  // finance — quote the CHEAPEST monthly on the card ("as low as"), which is
  // what the patient is deciding on. Pick by the RAW monthly, not the rounded
  // one, so two tiers a few cents apart can't tie into the wrong term.
  const tiers = b.tiers || [];
  if (!tiers.length) return null;
  let best = tiers[0];
  tiers.forEach((t) => { if (t.monthly < best.monthly) best = t; });
  return {
    headline: `As low as ~${fmt(Math.round(best.monthly))}/mo`,
    sub: `${todayPrefix}${best.months} months · estimate`,
  };
}

/**
 * Compute the dollar breakdown for a payment option against the patient's
 * out-of-pocket portion (the office-collectable the patient owes after
 * insurance). All money values are plain numbers; the caller formats them.
 *
 * `extras` (optional):
 *   downpayment — chairside "Pay today" $ amount; clamped to [0, totalDue].
 *
 * The breakdown carries `fee`/`feeLabel`/`totalDue`/`downpayment`/
 * `remainingAfterToday` on every kind so the renderer can show the
 * extra rows without re-doing the math. Split + finance per-payment
 * numbers are computed off `remainingAfterToday` (after the fee is added
 * and the downpayment is subtracted), which is the chairside-correct
 * math the TC walks the patient through.
 *
 * Returns a discriminated shape by `kind`:
 *   discount → { kind:'discount', base, fee, totalDue, discountPct, discountAmt, finalAmount, downpayment, remainingAfterToday }
 *   split    → { kind:'split',    base, fee, totalDue, count, perPayment, downpayment, remainingAfterToday }
 *   finance  → { kind:'finance',  base, fee, totalDue, apr, tiers:[{months,apr,kind,monthly}], months, monthly, downpayment, remainingAfterToday }
 *              (tiers[] is the multi-tier menu; months/monthly echo tiers[0] for back-compat)
 *              A Cherry-backed option (provider === 'cherry') returns tiers:[] / months:0 /
 *              monthly:0 / apr:0 and cherry:true — its figures come from Cherry's real plans
 *              (cherryTerms.js), never from a calculation here.
 *   custom   → { kind:'custom',   base, fee, totalDue, downpayment, remainingAfterToday }
 *
 * `base` is the patient portion echoed back so the UI can show "of $X".
 */
export function computePaymentBreakdown(opt, patientPortion, extras) {
  const base = Math.max(0, parseFloat(patientPortion) || 0);
  const fee = resolveFeeAmount(opt, base);
  const feeLabel = formatFeeLabel(opt);
  const totalDue = base + fee;

  // Downpayment only applies when the option offers it. For discount
  // (pay-in-full) we ignore it — pay in full IS the full payment.
  let downpayment = canTakeDownpayment(opt) ? Math.max(0, parseFloat(extras && extras.downpayment) || 0) : 0;
  if (downpayment > totalDue) downpayment = totalDue;
  const remainingAfterToday = Math.max(0, totalDue - downpayment);

  const common = { base, fee, feeLabel, totalDue, downpayment, remainingAfterToday };

  if (!opt) return { kind: 'custom', ...common };

  switch (opt.type) {
    case 'discount': {
      const pct = Math.max(0, parseFloat(opt.discount) || 0);
      // Discount applies to the total due (including any fee — e.g., a
      // CC processing fee on a pay-in-full option still gets the discount
      // applied to the principal, which is the patient-friendly read).
      const discountAmt = totalDue * (pct / 100);
      return {
        kind: 'discount',
        ...common,
        discountPct: pct,
        discountAmt,
        finalAmount: Math.max(0, totalDue - discountAmt),
      };
    }
    case 'split':
    case 'inhouse': {
      const count = Math.max(1, parseInt(opt.splits, 10) || 1);
      return { kind: 'split', ...common, count, perPayment: remainingAfterToday / count };
    }
    case 'finance': {
      // THE Cherry kill switch. A Cherry-backed option must never render the practice's typed
      // tiers: they understate the lender's real cost (see cherryTerms.js), and falling back to
      // them would do it while looking authoritative. Returning before they are built means no
      // surface — card, summary, patient view — can print one by accident.
      if (opt.provider === 'cherry') {
        return { kind: 'finance', ...common, apr: 0, tiers: [], months: 0, monthly: 0, cherry: true };
      }
      // Multi-tier financing: a Deferred Interest group (estimated at 0% over
      // the deferral) + a Reduced APR group sharing one APR. Each tier's
      // estimated monthly is computed off remainingAfterToday. Legacy
      // single-term options (months+apr) fall back to a single tier.
      const apr = Math.max(0, parseFloat(opt.apr) || 0);
      let deferred = Array.isArray(opt.deferredMonths) ? opt.deferredMonths.slice() : [];
      let aprMonths = Array.isArray(opt.aprMonths) ? opt.aprMonths.slice() : [];
      if (!deferred.length && !aprMonths.length) {
        const m = Math.max(1, parseInt(opt.months, 10) || 1);
        if (apr > 0) aprMonths = [m]; else deferred = [m];
      }
      const norm = (m) => Math.max(1, parseInt(m, 10) || 1);
      const amortize = (p, a, n) => {
        if (a > 0) { const r = a / 100 / 12; return (p * r) / (1 - Math.pow(1 + r, -n)); }
        return p / n;
      };
      const tiers = [
        ...deferred.map((m) => { const n = norm(m); return { months: n, apr: 0, kind: 'deferred', monthly: remainingAfterToday / n }; }),
        ...aprMonths.map((m) => { const n = norm(m); return { months: n, apr, kind: 'apr', monthly: amortize(remainingAfterToday, apr, n) }; }),
      ];
      // Back-compat: echo the first tier as the legacy single-tier fields so an
      // un-migrated consumer (or {months} token) still reads a sensible value.
      const primary = tiers[0] || { months: 1, monthly: remainingAfterToday };
      return { kind: 'finance', ...common, apr, tiers, months: primary.months, monthly: primary.monthly };
    }
    default:
      return { kind: 'custom', ...common };
  }
}

/**
 * The (single) enabled Cherry-backed option in `options` and the amount to price it at.
 *
 * Exactly the inputs the card uses: the patient portion (balance layer already folded in by the
 * caller), the option's own fee, and a chairside Pay today ONLY when this option is the chosen
 * one — the same rule optBreakdown applies, so the amount we ask Cherry to price is the amount
 * the card later matches against. Returns { opt, amount } (amount null when not finite), or null
 * when no enabled option is Cherry-backed.
 */
export function cherryPricingFor(options, patientPortion, chosenOptionId, chosenDownpayment) {
  const opt = (Array.isArray(options) ? options : []).find((o) => o && o.enabled && isCherryBacked(o));
  if (!opt) return null;
  const extras = opt.id === chosenOptionId ? { downpayment: chosenDownpayment } : undefined;
  return { opt, amount: cherryAmountFor(computePaymentBreakdown(opt, patientPortion, extras)) };
}
